import React, { useState } from "react";
import styles from "./styles.module.scss";
import DropdownButton from "app/components/Dropdown";
import { useTranslation } from "react-i18next";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { FilterState, StatsFilterOption, useGetSelectOptions, USGACourse, USGATee } from "../ScoresFilterModal/utils";
import ButtonSimple from "app/components/Button/ButtonSimple";
import { cls } from "app/utils";
import Select from "react-select";
import { SelectStyles } from "app/routes/Calendar/EventForm/constants";

interface Props {
  isModalOpen: boolean;
  modalHandler: () => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  coursesAndTees: USGACourse[];
  tees: USGATee[];
}

const StatsFilterModal = ({ isModalOpen, modalHandler, setFilters, coursesAndTees, tees }: Props) => {
  const { t } = useTranslation();
  const [selectableOptions] = useState<StatsFilterOption[]>(useGetSelectOptions());
  const [selectedOption, setSelectedOption] = useState<StatsFilterOption>(selectableOptions.find(option => option.value === 'recent_and_revision_scores'));
  const [selectedCourse, setSelectedCourse] = useState<USGACourse>({
    courseId: null,
    courseName: 'All Courses',
    tees: []
  });
  const [selectedTee, setSelectedTee] = useState<USGATee>({
    teeSetId: null,
    teeName: 'All Tees'
  });

  const onFilterModalClose = () => {
    setSelectedOption(selectableOptions.find(option => option.value === 'recent_and_revision_scores'));
    setSelectedCourse(coursesAndTees.find(option => option.courseId === null));
    setSelectedTee(tees.find(option => option.teeSetId === null));
    modalHandler();
  };

  const onApplyClick = () => {
    setFilters((prev) => ({
      ...prev,
      option: selectedOption.value !== 'scores_by_course_or_tee' ? selectedOption.value : null,
      course: selectedCourse?.courseId,
      tee: selectedTee?.teeSetId,
    }));
    modalHandler();
  };

  const onResetClick = () => {
    setFilters({
      option: selectableOptions.find(option => option.value === 'recent_and_revision_scores').value,
      course: null,
      tee: null,
    });
    onFilterModalClose();
  };

  return (
    <DropdownButton
        dropdownWrapperClass={styles.dropdown_container}
        isOpen={isModalOpen}
        onClose={onFilterModalClose}
    >
      <h1 className={styles.title}>{t('Filters')}</h1>

      <div className={styles.options_container}>
        {selectableOptions.map(option => (
          <ButtonSimple
            key={option.value}
            buttonText={option.title}
            onClick={() => setSelectedOption(option)}
            isActive={selectedOption.value === option.value}
            buttonStyle={cls(styles.option, { [styles.full_width]: option.value === 'recent_and_revision_scores' })}
          />
        ))}
      </div>

      {selectedOption.value === 'scores_by_course_or_tee' && (
        <>
          <div>
            <label>{t('Course')}</label>
            <Select
              styles={SelectStyles}
              placeholder={t('Select Course')}
              getOptionLabel={option => option.courseName}
              getOptionValue={option => option.courseId}
              options={coursesAndTees}
              isSearchable={false}
              value={coursesAndTees.find((option) => {
                return option.courseId === selectedCourse?.courseId;
              })}
              onChange={(selectedOption) => {
                setSelectedCourse(selectedOption as USGACourse);
              }}
            />
          </div>
          {selectedCourse?.courseId !== null && (
            <div>
              <label>{t('Tee')}</label>
              <Select
                styles={SelectStyles}
                placeholder={t('Select Tee')}
                getOptionLabel={option => option.teeName}
                getOptionValue={option => option.teeSetId}
                options={tees}
                isSearchable={false}
                value={tees.find((option) => {
                  return option.teeSetId === selectedTee?.teeSetId;
                })}
                onChange={(selectedOption) => {
                  setSelectedTee(selectedOption as USGATee);
                }}
              />
            </div>
          )}
        </>
      )}

      <div className={styles.buttons_container}>
        <ButtonVariant
          title={t("Reset")}
          buttonType="secondary-outline"
          onClickFunc={onResetClick}
        />
        <ButtonVariant
          title={t("Apply")}
          buttonType="primary"
          onClickFunc={onApplyClick}
        />
      </div>
    </DropdownButton>
  );
};

export default StatsFilterModal;
